// noinspection GraphQLUnresolvedReference

import {gql, TypedDocumentNode} from "@apollo/client";
import {
    ArtworkType,
    BrandType,
    BulletPointType,
    CommentType,
    ContactGroupType,
    CorrespondenceType,
    CrossSellType,
    DisclaimerType,
    DynamicDimensionsType,
    EnvironmentalClaimType,
    GuidelineType,
    ImageType,
    NotifyProductType,
    Nullable,
    NullableProductOptionType,
    NullableProductOptionValueType,
    PageInfoType,
    PaginationType,
    PriceType,
    ProductMediaType,
    ProductOptionType,
    ProductSpecificationType,
    PromotionType,
    QuestionEdgeType,
    QuestionStatisticsType,
    QuestionType,
    RatingStatisticsType,
    RatingType,
    RecommendedProductType,
    ResourceType,
    SpecificationType,
    StockInformationType,
    VideoType, YouTubeVideoThumbnailType
} from "./types";
import {ArtworkGuidelineType, ItemClassificationType, ItemType, PrivacyStatusType, UploadStatusType} from "./enums";

const PRODUCT_OPTION_VALUE_FRAGMENT: TypedDocumentNode<NullableProductOptionValueType> = gql`
    fragment ProductOptionValueFragment on ProductOptionValue {
        isAvailable
        isActive
        productAttributeTypeId
        value
        thumbnailImage {
            alt
            description
            filename
            isRaw
            title
            url
            width
            type
            isWide
            height
        }
        items {
            productId
            primaryId
            sku
            title
            path
        }
    }
`;

const PRODUCT_OPTION_FRAGMENT: TypedDocumentNode<NullableProductOptionType> = gql`
    ${PRODUCT_OPTION_VALUE_FRAGMENT}
    fragment ProductOptionFragment on ProductOption {
        isThumbnailable
        name
        productAttributeTypeId
        sortOrder
        units
        values {
            ...ProductOptionValueFragment
        }
    }
`;

const SPECIFICATION_FRAGMENT: TypedDocumentNode<SpecificationType> = gql`
    fragment SpecificationFragment on Specification {
        delimiter
        groupName
        types
        values
    }
`;

export const RESOURCE_FRAGMENT: TypedDocumentNode<ResourceType> = gql`
    fragment ResourceFragment on Resource {
        id
        name
        price(currencyCode: $currencyCode)
        title
        type
    }
`;

export const RATING_STATISTICS_FRAGMENT: TypedDocumentNode<RatingStatisticsType> = gql`
    fragment RatingStatisticsFragment on RatingStatistics {
        id
        type
        count
        sum
        min
        max
        range
        mean
        median
        mode
        standardDeviation
        distribution {
            id
            type
            oneStar
            twoStar
            threeStar
            fourStar
            fiveStar
        }
    }
`;

export const ITEM_RATINGS_STATISTICS_FRAGMENT: TypedDocumentNode<{
    id: string
    type: ItemClassificationType
    stats: RatingStatisticsType
}> = gql`
    ${RATING_STATISTICS_FRAGMENT}
    fragment ItemRatingsStatisticsFragment on ItemRatings {
        id
        type
        stats {
            ...RatingStatisticsFragment
        }
    }
`;

export const QUESTION_STATISTICS_FRAGMENT: TypedDocumentNode<QuestionStatisticsType> = gql`
    fragment QuestionStatisticsFragment on QuestionStatistics {
        id
        type
        count
        answeredCount
        unansweredCount
    }
`;

export const ITEM_QUESTIONS_STATISTICS_FRAGMENT: TypedDocumentNode<{
    id: string
    type: ItemClassificationType
    stats: QuestionStatisticsType
}> = gql`
    ${QUESTION_STATISTICS_FRAGMENT}
    fragment ItemQuestionsStatisticsFragment on ItemQuestions {
        id
        type
        stats {
            ...QuestionStatisticsFragment
        }
    }
`;

export const COMMENT_FRAGMENT: TypedDocumentNode<CommentType> = gql`
    fragment CommentFragment on Comment {
        id
        referenceId
        accountId
        internal
        name
        text
        visible
        created
        updated
    }
`;

export const PRODUCT_FRAGMENT: TypedDocumentNode<{
    artworkGuidelineType?: ArtworkGuidelineType,
    artworkGuidelineTypeId?: number,
    comparisonId?: number,
    componentDescription?: string,
    customizedId: number,
    customizedItemType: ItemType,
    customizedPath?: string,
    customizedUrl?: string,
    id: number,
    installationConsultation: boolean,
    isDesignerProduct: boolean,
    isOnline: boolean,
    isPurchasable: boolean,
    longDescription: string,
    shortDescription: string,
    sku: string,
    slug?: string,
    stockInformation: Nullable<StockInformationType>,
    title: string,
    validated: boolean,
    variantPath: string,
    primaryProductId?: number
}> = gql`
    fragment ProductFragment on Product {
        id
        primaryId
        primaryProductId
        sku
        slug
        title
        variantPath
        artworkGuidelineType
        artworkGuidelineTypeId
        installationConsultation
        componentDescription
        customizedId
        customizedItemType
        customizedPath
        customizedUrl
        longDescription
        shortDescription
        collectionName
        isDesignerProduct
        comparisonId
        isOnline
        isPurchasable
        validated
        stockInformation {
            minQuantity: minSaleQuantity
            status
            hasValidStatus
        }
    }
`;

export const STOCK_INFORMATION_FRAGMENT: TypedDocumentNode<{
    isTruckShipment: boolean,
    stockInformation: Nullable<StockInformationType>
}> = gql`
    fragment StockInformationFragment on Product {
        isTruckShipment
        stockInformation {
            artworkLeadTime
            artworkType
            id
            isAutoOffline
            isBackOrderable
            isBuiltToOrder
            isHardMinimumOrder
            isInStock
            isInTransit
            isNewProduct
            inTransitDays
            inTransitQuantity
            isOutOfStockNoBackOrder
            leadIn
            leadOut
            minQuantity: minSaleQuantity
            productType
            quantityOnHand
            shipCutoffDate
            shipEstimatedDate
            status
            vendorCode
        }
    }
`;

export const PRODUCT_DYNAMIC_DIMENSIONS_FRAGMENT: TypedDocumentNode<DynamicDimensionsType> = gql`
    fragment ProductDynamicDimensionsFragment on Product {
        id
        isDynamicDimension
        minWidth
        maxWidth
        minHeight
        maxHeight
    }
`;

export const PRODUCT_ENVIRONMENTAL_FRAGMENT: TypedDocumentNode<{
    environmentalScore: number,
    isEnvironmentallyConscious: boolean
}> = gql`
    fragment ProductEnvironmentFragment on Product {
        id
        environmentalScore
        isEnvironmentallyConscious
    }
`;

export const PROMOTION_FRAGMENT: TypedDocumentNode<PromotionType> = gql`
    fragment PromotionFragment on Promotion {
        type
        campaign
        description
        discountFlat
        discountPercent
        minAmount
        minQuantity
        statusCode
    }
`;

export const PRODUCT_SPECIFICATION_FRAGMENT: TypedDocumentNode<ProductSpecificationType> = gql`
    ${SPECIFICATION_FRAGMENT}
    fragment ProductSpecificationsFragment on ProductSpecifications {
        productId,
        specifications {
            ...SpecificationFragment
        }
    }
`;

export const GUIDELINE_FRAGMENT: TypedDocumentNode<GuidelineType> = gql`
    fragment GuidelineFragment on Guideline {
        id
        filename
        url
    }
`;

export const VIDEO_FRAGMENT: TypedDocumentNode<VideoType> = gql`
    fragment VideoFragment on Video {
        id
        type
        filename
        title
        description
        width
        height
        duration
        url
        thumbnailUrl
    }
`;

export const IMAGE_FRAGMENT: TypedDocumentNode<ImageType> = gql`
    fragment ImageFragment on Image {
        id
        type
        filename
        alt
        title
        description
        isRaw
        isWide
        width
        height
        url
        zoomUrl
        previewUrl
        thumbnailUrl
    }
`;

export const PRODUCT_MEDIA_FRAGMENT: TypedDocumentNode<ProductMediaType> = gql`
    ${IMAGE_FRAGMENT}
    ${VIDEO_FRAGMENT}
    ${GUIDELINE_FRAGMENT}
    fragment ProductMediaFragment on ItemMedia {
        itemId
        itemType
        images {
            itemId
            itemType
            raw {
                ...ImageFragment
            }
            dot4b {
                ...ImageFragment
            }
        }
        videos {
            productIntro {
                ...VideoFragment
            }
            productSeriesShowcase {
                ...VideoFragment
            }
            productOnly {
                ...VideoFragment
            }
            assemblyHowTo{
                ...VideoFragment
            }
        }
        guidelines {
            artworkTemplate {
                ...GuidelineFragment
            }
            instructions {
                ...GuidelineFragment
            }
            universal {
                ...GuidelineFragment
            }
            warranty {
                ...GuidelineFragment
            }
        }
    }
`;

export const PRODUCT_OPTIONS_FRAGMENT: TypedDocumentNode<ProductOptionType[]> = gql`
    ${PRODUCT_OPTION_FRAGMENT}
    fragment ProductOptionsFragment on Product {
        productOptions {
            ...ProductOptionFragment
        }
    }
`;

export const PRODUCT_RESOURCES_FRAGMENT: TypedDocumentNode<ResourceType[]> = gql`
    ${RESOURCE_FRAGMENT}
    fragment ResourcesFragment on Product {
        resources {
            ...ResourceFragment
        }
    }
`;

export const PRODUCT_CROSS_SELL_FRAGMENT: TypedDocumentNode<CrossSellType> = gql`
    ${RATING_STATISTICS_FRAGMENT}
    fragment CrossSellFragment on CrossSell {
        productId
        primaryId
        designerId
        sku
        title
        customizeIt
        path
        imageSrc
        imageAlt
        imageTitle
        dimensionalPrice
        minQuantity
        price
        unitPrice
        onSale
        salePrice
        saleAmount
        salePercentage
        ratingStatistics {
            ...RatingStatisticsFragment
        }
    }
`;

export const PRODUCT_RECOMMENDATION_FRAGMENT: TypedDocumentNode<RecommendedProductType> = gql`
    ${RATING_STATISTICS_FRAGMENT}
    fragment RecommendationFragment on Recommendation {
        similarityValue
        productId
        primaryId
        sku
        title
        customizeIt
        path
        imageSrc
        imageAlt
        imageTitle
        dimensionalPrice
        price
        unitPrice
        onSale
        salePrice
        saleAmount
        salePercentage
        minQuantity
        ratingStatistics {
            ...RatingStatisticsFragment
        }
    }
`;

export const PAGE_INFO_FRAGMENT: TypedDocumentNode<PageInfoType> = gql`
    fragment PageInfoFragment on PageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
    }
`;

export const QUESTION_FRAGMENT: TypedDocumentNode<QuestionType> = gql`
    ${COMMENT_FRAGMENT}
    fragment QuestionFragment on Question {
        id
        name
        text
        visible
        created
        answers {
            id
            name
            text
            internal
            created
            comments {
                ...CommentFragment
            }
        }
    }
`;

export const PRICE_FRAGMENT: TypedDocumentNode<PriceType> = gql`
    fragment PriceFragment on ProductPrice {
        id
        lowRange
        highRange
        price
        unitPrice
        salePrice
        saleAmount
        salePercentage
        onSale
        width
        height
    }
`;

export const CONTACT_GROUP_FRAGMENT: TypedDocumentNode<ContactGroupType> = gql`
    fragment ContactGroupFragment on ContactGroup {
        type
        email
        phoneNumber
    }
`;

export const DISCLAIMER_FRAGMENT: TypedDocumentNode<DisclaimerType> = gql`
    fragment DisclaimerFragment on Disclaimer {
        id
        sku
        chemical
        productId
        toxicityType
        materialCode
        materialDescription
    }
`;

export const ENVIRONMENTAL_CLAIMS_FRAGMENT: TypedDocumentNode<EnvironmentalClaimType> = gql`
    fragment EnvironmentalClaimsFragment on EnvironmentalClaim {
        id
        sku
        productId
        description
    }
`;

export const PRODUCT_CALLOUT_FRAGMENT: TypedDocumentNode<{ callOut?: string }> = gql`
    fragment ProductCalloutFragment on Product {
        callOut
    }
`;

export const BRAND_FRAGMENT: TypedDocumentNode<BrandType> = gql`
    ${IMAGE_FRAGMENT}
    fragment BrandFragment on ProductBrand {
        id
        name
        description
        link
        image {
            ...ImageFragment
        }
    }
`;

export const BULLET_POINT_FRAGMENT: TypedDocumentNode<BulletPointType> = gql`
    fragment BulletPointFragment on BulletPoint {
        id
        description
    }
`;

export const ARTICLE_FRAGMENT: TypedDocumentNode<CorrespondenceType> = gql`
    fragment ArticleFragment on Article {
        id
        title
        path
    }
`;

export const GUIDE_FRAGMENT: TypedDocumentNode<CorrespondenceType> = gql`
    fragment GuideFragment on Guide {
        id
        title
        path
    }
`;

export const CREATE_ARTWORK_PAYLOAD_FRAGMENT: TypedDocumentNode<ArtworkType> = gql`
    fragment CreateArtworkPayloadFragment on CreateArtworkPayload {
        artwork {
            id
            accountId
            filename
            url
            thumbnail
        }
    }
`;

export const RATING_PAGINATION_FRAGMENT: TypedDocumentNode<{
    pageInfo: PaginationType
    ratings: RatingType[]
}> = gql`
    ${COMMENT_FRAGMENT}
    fragment RatingPaginationFragment on RatingPagination {
        id
        type
        pageInfo {
            pageNumber
            pageSize
            totalItemCount
            totalPageCount
            hasNextPage
            hasPreviousPage
            isFirstPage
            isLastPage
            lastPage
        }
        ratings {
            id
            orderNumber
            productId
            primaryId
            accountId
            name
            grade
            visible
            created
            review {
                id
                ratingId
                sentiment
                helpfulCount
                title
                text
                visible
                created
                updated
                media {
                    id
                    ratingId
                    reviewId
                    filename
                    url
                    width
                    height
                    visible
                    created
                    updated
                }
            }
            comments {
                ...CommentFragment
            }
            attributes {
                key
                value
                unit
            }
        }
    }
`;

export const ITEM_REVIEW_MEDIA_CONNECTION_FRAGMENT: TypedDocumentNode<{
    id: string
    type: ItemClassificationType
}> = gql`
    ${COMMENT_FRAGMENT}
    ${PAGE_INFO_FRAGMENT}
    fragment ReviewMediaConnectionFragment on ReviewMediaConnection {
        id
        type
        media {
            id
            url
            visible
            filename
            ratingId
            reviewId
            created
            review {
                id
                text
                title
                visible
                rating {
                    id
                    orderNumber
                    productId
                    primaryId
                    accountId
                    name
                    grade
                    visible
                    created
                    comments {
                        ...CommentFragment
                    }
                    attributes {
                        key
                        value
                        unit
                    }
                }
            }
        }
        pageInfo {
            ...PageInfoFragment
        }
    }
`;

export const NOTIFY_PRODUCT_FRAGMENT: TypedDocumentNode<NotifyProductType> = gql`
    fragment NotifyProductFragment on CreateNotifyProductPayload {
        notifyProduct {
            isSuccessful
        }
    }
`;

export const ITEM_QUESTION_CONNECTION_FRAGMENT: TypedDocumentNode<{
    id: string
    type: ItemClassificationType,
    edges: QuestionEdgeType[]
    pageInfo: PageInfoType
}> = gql`
    ${QUESTION_FRAGMENT}
    ${PAGE_INFO_FRAGMENT}
    fragment QuestionConnectionFragment on QuestionConnection {
        id
        type
        edges {
            cursor
            node {
                ...QuestionFragment
            }
        }
        pageInfo {
            ...PageInfoFragment
        }
    }
`;

export const YOUTUBE_VIDEO_THUMBNAIL_FRAGMENT: TypedDocumentNode<YouTubeVideoThumbnailType> = gql`
    fragment YouTubeVideoThumbnailFragment on YouTubeThumbnail {
        url
        width
        height
    }
`;

export const YOUTUBE_VIDEO_THUMBNAIL_COLLECTION_FRAGMENT: TypedDocumentNode<{
    id: string
    defaultResolution: YouTubeVideoThumbnailType
    mediumResolution: YouTubeVideoThumbnailType
    highResolution: YouTubeVideoThumbnailType
    standardResolution: Nullable<YouTubeVideoThumbnailType>
    maxResolution: Nullable<YouTubeVideoThumbnailType>
}> = gql`
    ${YOUTUBE_VIDEO_THUMBNAIL_FRAGMENT}
    fragment YouTubeVideoThumbnailCollectionFragment on YouTubeThumbnailCollection {
        id
        defaultResolution {
            ...YouTubeVideoThumbnailFragment
        }
        mediumResolution {
            ...YouTubeVideoThumbnailFragment
        }
        highResolution {
            ...YouTubeVideoThumbnailFragment
        }
        standardResolution {
            ...YouTubeVideoThumbnailFragment
        }
        maxResolution {
            ...YouTubeVideoThumbnailFragment
        }
    }
`;

export const YOUTUBE_VIDEO_FRAGMENT: TypedDocumentNode<{
    id: string
    title: string
    description?: string
    uploadStatus: UploadStatusType
    privacyStatus: PrivacyStatusType
    thumbnailCollection: {
        id: string
        defaultResolution: YouTubeVideoThumbnailType
        mediumResolution: YouTubeVideoThumbnailType
        highResolution: YouTubeVideoThumbnailType
        standardResolution: Nullable<YouTubeVideoThumbnailType>
        maxResolution: Nullable<YouTubeVideoThumbnailType>
    }
}> = gql`
    ${YOUTUBE_VIDEO_THUMBNAIL_COLLECTION_FRAGMENT}
    fragment YouTubeVideoFragment on YouTubeVideo {
        id
        title
        description
        uploadStatus
        privacyStatus
        thumbnailCollection {
            ...YouTubeVideoThumbnailCollectionFragment
        }
    }
`;